import { type FunctionComponent, useContext, useEffect } from 'react';
import { type ProductStatusEnum } from '../../../__generated__/graphql-client-types';
import { MobileVariantSelector } from '../../../components/product-components/mobile-variant-selector/mobile-variant-selector.component';
import { ProductStock } from '../../../components/product-components/product-stock/product-stock.component';
import { VariantSelector } from '../../../components/product-components/variant-selector/variant-selector.component';
import { ProductFamilyContext } from '../../../contexts/product-family/product-family.context';
import { VariantContext } from '../../../contexts/variant/variant.context';
import { filterVariantsByStatuses } from '../../../helpers/product-helper/product.helper';
import { useSiteViewPreference } from '../../../hooks/apollo/employee/employee.hooks';
import { useNotification } from '../../../hooks/notification/notification.hooks';

export type ProductFinishProps = {
	isPDP?: boolean;
	nonStockMode?: boolean;
	productUid?: number;
	isRegionallyOutOfStock?: boolean;
};

export const ProductFinish: FunctionComponent<ProductFinishProps> = ({
	isPDP,
	nonStockMode,
	productUid,
	isRegionallyOutOfStock = false
}) => {
	const { selectedVariant, currentVariant, setSelectedVariant, confirmSelectedVariant } = useContext(VariantContext);
	const { productFamily } = useContext(ProductFamilyContext);
	const { notifyDanger } = useNotification();
	const { showAsEmployee } = useSiteViewPreference();

	const variantStatus: ProductStatusEnum[] = ['stock'];
	if (nonStockMode) {
		variantStatus.push('nonstock');
		if (showAsEmployee) {
			variantStatus.push('pending');
		}
	}
	// This will determine which finishes are visible/selectable as thumbnails
	const selectableVariants = filterVariantsByStatuses(productFamily?.variants ?? [], variantStatus);

	/* Force selected variant to be one of the rendered finish thumbnails */
	useEffect(() => {
		const isSelectedVariantLegal = selectableVariants.some((legalVariant) => legalVariant.id === selectedVariant.id);
		if (!isSelectedVariantLegal) {
			if (selectedVariant.status === 'discontinued') {
				const discontinuedVariant = productFamily?.variants.find((variant) => variant.id === productUid);
				if (discontinuedVariant) {
					notifyDanger(
						`The "${discontinuedVariant?.title}" finish has been discontinued and is no longer available for purchase.`,
						0
					);
				}
			}
			setSelectedVariant(selectableVariants[0]);
		} else {
			// initial selected variant should be locked in at this point
			confirmSelectedVariant();
		}
	}, [selectedVariant.id]);
	return (
		<>
			{productFamily && selectableVariants.length > 1 ? (
				<>
					<div className={`${isPDP ? 'dn db-ns f5-ns lh-copy pt3-ns' : 'ma3 ma0-ns mt1-ns dn db-ns f7 f5-ns lh-copy'}`}>
						{currentVariant.title !== 'N/A' && (
							<>
								<strong>Finish: </strong>
								<span data-automation="finish-name" data-testid={`finish-${currentVariant.title}`}>
									{currentVariant.title ?? `Choose Finish`}
								</span>
								<span className="theme-grey-darker"> -</span>
							</>
						)}
						<ProductStock
							count={currentVariant.count}
							isMadeToOrder={currentVariant.isMadeToOrder}
							className={'dib-ns'}
							isRegionallyOutOfStock={isRegionallyOutOfStock}
							brand={productFamily.brandInfo.name}
						/>
						<VariantSelector variants={selectableVariants} familyId={productFamily.id} />
					</div>
					<div className={`${isPDP ? 'db dn-ns' : 'db dn-ns mt3 f7 f5-ns'}`}>
						<div className={`${isPDP ? 'f5 mt3' : ''}`}>
							<strong>Finish: </strong>
							<span data-automation="finish-name">{currentVariant.title ?? `Choose Finish`}</span>
							<span className="theme-grey-darker"> -</span>
							<ProductStock
								count={currentVariant.count}
								isMadeToOrder={currentVariant.isMadeToOrder}
								className={'dib'}
								isRegionallyOutOfStock={isRegionallyOutOfStock}
								brand={productFamily.brandInfo.name}
							/>
							<MobileVariantSelector variants={selectableVariants} familyId={productFamily.id} />
						</div>
					</div>
				</>
			) : (
				<div className={`${isPDP ? 'mt3 pa0-ns mt1-ns f5 lh-copy-ns bn-ns' : 'ma3 ma0-ns mlt-ns f7 f5-ns lh-copy'}`}>
					{currentVariant.title !== 'N/A' && (
						<>
							<strong>Finish:</strong>
							<span data-automation="finish-name"> {currentVariant.title}</span>
							<span className="theme-grey-darker"> -</span>
						</>
					)}
					<ProductStock
						count={currentVariant.count}
						isMadeToOrder={currentVariant.isMadeToOrder}
						className={'dib'}
						isRegionallyOutOfStock={isRegionallyOutOfStock}
						brand={productFamily.brandInfo.name}
					/>
				</div>
			)}
		</>
	);
};
